'use client';

import { cn } from '~/utils';
import { HeaderAiSearchBar } from '~/storyblok/components/HeaderAiSearchBar';
import { SearchModeToggle } from './SearchModeToggle';
import type { ActivitySearchMode } from './shared';

type Props = {
    mode: ActivitySearchMode;
    onModeChange: (mode: ActivitySearchMode) => void;
    placeholderPrefix: string;
    suggestions: readonly string[];
    aiSearchLabel: string;
    ariaLabel: string;
    /** Navigate to this URL on submit (homepage). Ignored when `onSubmit` is set. */
    href?: string;
    /** Handle submit in-place instead of navigating (search page composer). */
    onSubmit?: (value: string) => void;
    /** Seed the input (e.g. a prompt carried over from another page). */
    initialValue?: string;
    /** Control rendered just before the submit button (e.g. the chat-history dropdown). */
    trailing?: React.ReactNode;
    /** Pin the Filters ⇄ AI toggle inside the bar as the leading control. Defaults to true;
     *  the homepage turns it off on mobile, where the toggle sits above the pill instead. */
    showLeadingToggle?: boolean;
    className?: string;
};

/**
 * The single AI search bar used on BOTH the homepage and the `/teambuildings` hero, so the two
 * stay pixel-identical. It is the {@link HeaderAiSearchBar} with the Filters ⇄ AI toggle pinned
 * inside it as the leading control (no separator) — callers only differ in what submitting does
 * (navigate via `href` vs start a chat via `onSubmit`) and whether a trailing control is shown.
 * The caller provides the white pill wrapper.
 */
export const AiSearchBar: React.FC<Props> = ({
    mode,
    onModeChange,
    className,
    showLeadingToggle = true,
    ...bar
}) => (
    <HeaderAiSearchBar
        surface="light"
        className={cn('flex-1', className)}
        href=""
        leading={
            showLeadingToggle ? (
                <SearchModeToggle mode={mode} onChange={onModeChange} theme="light" />
            ) : undefined
        }
        {...bar}
    />
);
